import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UtensilsCrossed, Loader2, Plus, Trash2, Edit3, Save,
  ChevronDown, ChevronUp, X, BookOpen, IceCream, Flame, ChefHat, Cake
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Mezze Froid', 'Mezze Chaud', 'Plats', 'Dessert'];
const CAT_CONFIG = {
  'Mezze Froid': { icon: IceCream, color: 'text-blue-600', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  'Mezze Chaud': { icon: Flame, color: 'text-red-600', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  'Plats': { icon: ChefHat, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300' },
  'Dessert': { icon: Cake, color: 'text-neon-pink', bg: 'bg-neon-pink/10', border: 'border-neon-pink/30' },
};

const MenuPage = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_manager;
  const [menus, setMenus] = useState([]);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  // Dialogs
  const [itemDialog, setItemDialog] = useState({ open: false, item: null });
  const [menuDialog, setMenuDialog] = useState({ open: false, menu: null });
  const [itemForm, setItemForm] = useState({ name: '', description: '', category: 'Mezze Froid' });
  const [menuForm, setMenuForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const fetchMenus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/menu/`);
      const m = res.data.data || [];
      setMenus(m);
      if (m.length > 0 && !activeMenuId) setActiveMenuId(m[0].id);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }, [activeMenuId]);

  const fetchItems = useCallback(async () => {
    if (!activeMenuId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/menu/${activeMenuId}/items`);
      setItems(res.data.data || []);
      const expanded = {};
      CATEGORIES.forEach(c => { expanded[c] = false; });
      setExpandedCats(expanded);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [activeMenuId]);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  // Menu CRUD
  const openMenuDialog = (menu = null) => {
    setMenuForm(menu ? { name: menu.name, description: menu.description || '' } : { name: '', description: '' });
    setMenuDialog({ open: true, menu });
  };

  const handleSaveMenu = async () => {
    if (!menuForm.name.trim()) return;
    try {
      setSaving(true);
      if (menuDialog.menu) {
        await axios.put(`${API}/menu/${menuDialog.menu.id}`, menuForm);
        toast.success('Menu modifié');
      } else {
        const res = await axios.post(`${API}/menu/`, menuForm);
        setActiveMenuId(res.data.data.id);
        toast.success('Menu créé');
      }
      setMenuDialog({ open: false, menu: null });
      fetchMenus();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMenu = async (menuId) => {
    try {
      await axios.delete(`${API}/menu/${menuId}`);
      toast.success('Menu supprimé');
      if (activeMenuId === menuId) setActiveMenuId(null);
      fetchMenus();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // Item CRUD
  const openItemDialog = (item = null) => {
    setItemForm(item ? { name: item.name, description: item.description, category: item.category } : { name: '', description: '', category: 'Mezze Froid' });
    setItemDialog({ open: true, item });
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) return;
    try {
      setSaving(true);
      if (itemDialog.item) {
        await axios.put(`${API}/menu/items/${itemDialog.item.id}`, itemForm);
        toast.success('Article modifié');
      } else {
        await axios.post(`${API}/menu/items`, { ...itemForm, menu_id: activeMenuId });
        toast.success('Article ajouté');
      }
      setItemDialog({ open: false, item: null });
      fetchItems();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/menu/items/${itemId}`);
      toast.success('Article supprimé');
      fetchItems();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const activeMenu = menus.find(m => m.id === activeMenuId);

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="menu-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4 pr-10 md:pr-0">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">Menu</h1>
            <p className="text-gray-500">La carte du restaurant</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)} className={`rounded-full border-gray-200 ${editMode ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-gray-700'}`} data-testid="admin-edit-toggle">
                <Edit3 size={14} className="mr-1.5" />{editMode ? 'Quitter édition' : 'Mode Admin'}
              </Button>
            </div>
          )}
        </div>

        {/* Menu tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {menus.map(menu => (
            <div
              key={menu.id}
              onClick={() => setActiveMenuId(menu.id)}
              role="button"
              tabIndex={0}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap text-sm font-medium transition-all cursor-pointer ${
                activeMenuId === menu.id
                  ? 'bg-neon-pink/10 text-neon-pink border border-neon-pink/30'
                  : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'
              }`}
              data-testid={`menu-tab-${menu.id}`}
            >
              <UtensilsCrossed size={16} />
              {menu.name}
              {editMode && (
                <span className="ml-1 flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); openMenuDialog(menu); }} className="text-gray-400 hover:text-amber-700"><Edit3 size={12} /></button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteMenu(menu.id); }} className="text-gray-400 hover:text-red-600"><Trash2 size={12} /></button>
                </span>
              )}
            </div>
          ))}
          {editMode && (
            <Button onClick={() => openMenuDialog()} variant="outline" size="sm" className="rounded-full bg-white border-dashed border-gray-300 text-gray-500" data-testid="add-menu-btn">
              <Plus size={14} className="mr-1" />Nouveau Menu
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-4" />
            <p className="text-gray-500">Chargement du menu...</p>
          </div>
        ) : !activeMenu ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">Aucun menu</h3>
            {isAdmin && <Button onClick={() => openMenuDialog()} className="bg-neon-pink hover:bg-neon-pink/90 rounded-full mt-4"><Plus size={16} className="mr-2" />Créer un menu</Button>}
          </div>
        ) : (
          <div className="space-y-6">
            {CATEGORIES.map(catName => {
              const catItems = groupedItems[catName] || [];
              const config = CAT_CONFIG[catName];
              const Icon = config.icon;
              const isExpanded = expandedCats[catName] === true;

              return (
                <div key={catName} className={`bg-white border ${config.border} rounded-2xl overflow-hidden`}>
                  <button onClick={() => setExpandedCats(prev => ({ ...prev, [catName]: !prev[catName] }))} className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${config.bg} rounded-2xl flex items-center justify-center`}>
                        <Icon className={config.color} size={24} />
                      </div>
                      <div className="text-left">
                        <h2 className="font-heading font-bold text-xl text-gray-900">{catName}</h2>
                        <span className="text-sm text-gray-400">{catItems.length} articles</span>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="text-gray-500" size={20} /> : <ChevronDown className="text-gray-500" size={20} />}
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <div className="border-t border-gray-100 divide-y divide-gray-100">
                          {catItems.map((item, idx) => (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: idx * 0.03 }}
                              className="px-6 py-4 hover:bg-gray-50 transition-colors group"
                              data-testid={`menu-item-${item.id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="font-heading font-bold text-gray-900 text-lg mb-1">{item.name}</h3>
                                  <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
                                </div>
                                {editMode && (
                                  <div className="flex gap-1 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => openItemDialog(item)} className="w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 flex items-center justify-center"><Edit3 size={14} /></button>
                                    <button onClick={() => handleDeleteItem(item.id)} className="w-8 h-8 rounded-lg text-red-600 hover:bg-red-500/10 flex items-center justify-center"><Trash2 size={14} /></button>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))}
                          {editMode && (
                            <div className="px-6 py-3">
                              <Button onClick={() => { setItemForm({ name: '', description: '', category: catName }); setItemDialog({ open: true, item: null }); }} variant="ghost" size="sm" className="text-gray-400 hover:text-neon-pink" data-testid={`add-item-${catName}`}>
                                <Plus size={14} className="mr-1" />Ajouter un article dans {catName}
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Item Dialog */}
      <Dialog open={itemDialog.open} onOpenChange={(open) => !open && setItemDialog({ open: false, item: null })}>
        <DialogContent className="sm:max-w-lg bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-gray-900">{itemDialog.item ? 'Modifier' : 'Ajouter'} un article</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-700">Nom du plat</Label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="Ex: Salade Mayrig" className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900" data-testid="item-name-input" />
            </div>
            <div>
              <Label className="text-gray-700">Description / Composition</Label>
              <Textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} placeholder="Ingrédients et composition du plat..." className="mt-1 rounded-xl bg-gray-100 border-gray-200 text-gray-900 min-h-[80px]" data-testid="item-desc-input" />
            </div>
            <div>
              <Label className="text-gray-700">Catégorie</Label>
              <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                <SelectTrigger className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setItemDialog({ open: false, item: null })} className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700">Annuler</Button>
            <Button onClick={handleSaveItem} disabled={saving || !itemForm.name.trim()} className="flex-1 bg-neon-pink hover:bg-neon-pink/90 rounded-full shadow-neon-pink" data-testid="save-item-btn">
              {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} className="mr-2" />Enregistrer</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Menu Dialog */}
      <Dialog open={menuDialog.open} onOpenChange={(open) => !open && setMenuDialog({ open: false, menu: null })}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-gray-900">{menuDialog.menu ? 'Modifier' : 'Créer'} un menu</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-700">Nom du menu</Label>
              <Input value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })} placeholder="Ex: Menu Déjeuner" className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900" data-testid="menu-name-input" />
            </div>
            <div>
              <Label className="text-gray-700">Description</Label>
              <Input value={menuForm.description} onChange={(e) => setMenuForm({ ...menuForm, description: e.target.value })} placeholder="Description optionnelle" className="mt-1 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900" />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMenuDialog({ open: false, menu: null })} className="flex-1 rounded-full bg-gray-100 border-gray-200 text-gray-700">Annuler</Button>
            <Button onClick={handleSaveMenu} disabled={saving || !menuForm.name.trim()} className="flex-1 bg-neon-pink hover:bg-neon-pink/90 rounded-full shadow-neon-pink" data-testid="save-menu-btn">
              {saving ? <Loader2 className="animate-spin" size={18} /> : 'Enregistrer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuPage;
